/**
 * Image storage operations for offline images
 * Handles saving, retrieving, and managing offline image blobs with quota management
 * Business logic layer for offline image handling
 */

import { v4 as uuidv4 } from "uuid";
import {
  deletePendingImage,
  getAllPendingImages,
  getPendingImage,
  getPendingImagesByQuote,
  getPendingImagesByStatus,
  savePendingImage,
  updateImageStatus,
} from "./db";
import { withQuotaHandling } from "./quota-handler";
import type { OfflineImage } from "./types";

/**
 * Save image blob to IndexedDB for offline storage
 * Automatically handles quota management
 */
export async function saveOfflineImage(imageData: {
  quote_id: string;
  field_name: string;
  component_id?: number;
  blob: Blob;
  file_name: string;
}): Promise<{ success: boolean; image?: OfflineImage; error?: string }> {
  try {
    const now = new Date().toISOString();
    const imageId = uuidv4();

    const offlineImage: OfflineImage = {
      id: imageId,
      quote_id: imageData.quote_id,
      field_name: imageData.field_name,
      component_id: imageData.component_id,
      blob: imageData.blob,
      file_name: imageData.file_name,
      file_size: imageData.blob.size,
      mime_type: imageData.blob.type,
      created_at: now,
      sync_status: "pending",
      retry_count: 0,
    };

    await withQuotaHandling(() => savePendingImage(offlineImage));
    return { success: true, image: offlineImage };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Failed to save offline image";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get a specific offline image by ID
 */
export async function getOfflineImage(
  id: string
): Promise<{ success: boolean; image?: OfflineImage; error?: string }> {
  try {
    const image = await getPendingImage(id);
    if (!image) {
      return { success: false, error: "Image not found" };
    }
    return { success: true, image };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Failed to get offline image";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get all offline images for a specific quote
 */
export async function getOfflineImagesByQuote(quoteId: string): Promise<OfflineImage[]> {
  try {
    return await getPendingImagesByQuote(quoteId);
  } catch (_error) {
    return [];
  }
}

/**
 * Get images pending sync (not yet uploaded)
 */
export async function getPendingSyncImages(): Promise<OfflineImage[]> {
  try {
    return await getPendingImagesByStatus("pending");
  } catch (_error) {
    return [];
  }
}

/**
 * Get count of images pending sync
 */
export async function getPendingSyncImageCount(): Promise<number> {
  try {
    const images = await getPendingSyncImages();
    return images.length;
  } catch (_error) {
    return 0;
  }
}

/**
 * Mark image as syncing (upload in progress)
 */
export async function markImageSyncing(id: string): Promise<void> {
  await updateImageStatus(id, "syncing");
}

/**
 * Mark image as synced (successfully uploaded)
 * Stores the R2 URL and marks for deletion
 */
export async function markImageSynced(id: string, uploadedUrl: string): Promise<void> {
  await updateImageStatus(id, "synced", undefined, uploadedUrl);
}

/**
 * Mark image sync as failed
 * Increments retry count
 */
export async function markImageFailed(id: string, error: string): Promise<void> {
  const image = await getPendingImage(id);
  if (!image) {
    return;
  }

  image.retry_count = (image.retry_count || 0) + 1;
  image.sync_status = "failed";
  image.last_error = error;

  await savePendingImage(image);
}

/**
 * Delete an offline image (and free up quota)
 */
export async function deleteOfflineImage(
  id: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await deletePendingImage(id);
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Failed to delete offline image";
    return { success: false, error: errorMessage };
  }
}

/**
 * Get image storage statistics
 */
export async function getImageStats(): Promise<{
  total: number;
  pending: number;
  syncing: number;
  synced: number;
  failed: number;
  totalSize: number;
}> {
  try {
    const images = await getAllPendingImages();

    const totalSize = images.reduce((sum, img) => sum + img.file_size, 0);

    return {
      total: images.length,
      pending: images.filter((img) => img.sync_status === "pending").length,
      syncing: images.filter((img) => img.sync_status === "syncing").length,
      synced: images.filter((img) => img.sync_status === "synced").length,
      failed: images.filter((img) => img.sync_status === "failed").length,
      totalSize,
    };
  } catch (_error) {
    return { total: 0, pending: 0, syncing: 0, synced: 0, failed: 0, totalSize: 0 };
  }
}

/**
 * Clean up synced images to free space
 * Removes images that have been successfully uploaded
 */
export async function cleanupSyncedImages(): Promise<number> {
  try {
    const syncedImages = await getPendingImagesByStatus("synced");
    let deletedCount = 0;

    for (const image of syncedImages) {
      await deletePendingImage(image.id);
      deletedCount++;
    }
    return deletedCount;
  } catch (_error) {
    return 0;
  }
}
